import { useState, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { evaluateArchivoStream, type RetroSessionPayload } from "@/features/evaluacion/hooks/useRetroApi";
import { useCreateIntento } from "@/hooks/intentos/useIntentos";
import { EvaluacionService } from "../services/evaluacion.service";
import type { EvaluacionContext } from "../types/evaluacion.types";
import { useCurrentUser } from "@/features/login/hooks/useCurrentUser";

export interface UseEvaluacionStreamingOptions {
  context: EvaluacionContext;
  onStreamStart?: () => void;
  onFirstChunk?: () => void;
  onComplete?: (sessionId: string) => void;
  onError?: (error: Error) => void;
}

export interface UseEvaluacionStreamingReturn {
  evaluarConStreaming: (file: File) => Promise<void>;
  feedback: string;
  retroContent: string;
  isStreaming: boolean;
  sessionId: string | null;
  error: string | null;
  resetState: () => void;
}

export function useEvaluacionStreaming({
  context,
  onStreamStart,
  onFirstChunk,
  onComplete,
  onError: onErrorCallback,
}: UseEvaluacionStreamingOptions): UseEvaluacionStreamingReturn {
  const queryClient = useQueryClient();
  const { mutateAsync: createIntento } = useCreateIntento();
  const { usuarioInfo } = useCurrentUser();
  const [feedback, setFeedback] = useState<string>("");
  const [retroContent, setRetroContent] = useState<string>("");
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const firstChunkCalledRef = useRef<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const evaluationPayloadRef = useRef<RetroSessionPayload | null>(null);
  const resetState = useCallback(() => {
    setFeedback("");
    setRetroContent("");
    setIsStreaming(false);
    setSessionId(null);
    setError(null);
    firstChunkCalledRef.current = false;
  }, []);

  const evaluarConStreaming = useCallback(
    async (file: File): Promise<void> => {
      try {
        setError(null);
        setRetroContent("");
        firstChunkCalledRef.current = false;
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;
        setFeedback("Subiendo archivo...");
        const uploadResult = await EvaluacionService.uploadFile({
          file,
          actividadId: context.actividadId,
        });
        if (!uploadResult?.llave && !uploadResult?.url) {
          throw new Error("No se pudo obtener la llave o URL del archivo subido");
        }
        setFeedback("Archivo subido. Generando retroalimentación...");
        setIsStreaming(true);
        onStreamStart?.();
        let accumulatedText = "";
        evaluationPayloadRef.current = null;
        await evaluateArchivoStream(
          {
            file: uploadResult.llave ?? uploadResult.url,
            file_clean: context.archivoLimpioKey ?? context.archivoLimpioUrl,
            index: context.cerebro,
            programa: context.programa,
            actividad: context.actividadId,
          },
          {
            onMessage: (message) => {
              setFeedback(message);
            },
            onChunk: (chunk) => {
              accumulatedText += chunk;
              setRetroContent(accumulatedText);
              if (!firstChunkCalledRef.current && accumulatedText.length > 0) {
                firstChunkCalledRef.current = true;
                onFirstChunk?.();
              }
            },
            onComplete: (payload) => {
              evaluationPayloadRef.current = payload as RetroSessionPayload;
            },
            onError: (err) => {
              throw err;
            },
          },
          controller.signal
        );
        
        const evaluationPayload = evaluationPayloadRef.current as RetroSessionPayload | null;
        if (!evaluationPayload || !evaluationPayload.base_feedback) {
          throw new Error("No se recibió retroalimentación del servidor");
        }
        setFeedback("Retroalimentación generada. Registrando intento...");
        await createIntento({
          idActividadAprendiz: context.actividadAprendizId,
          archivo: uploadResult,
          retro: evaluationPayload.base_feedback,
          sessionId: evaluationPayload.session_id,
          sessionPayload: JSON.stringify(evaluationPayload),
          preguntasSugeridas: evaluationPayload.suggested_questions,
        });
        await queryClient.invalidateQueries({
          queryKey: [
            "actividadAprendiz",
            context.actividadId,
            context.aprendizId,
          ],
        });
        // Invalidar también la query de usuarioInfo para actualizar los intentos en la página principal
        if (usuarioInfo?.id) {
          await queryClient.invalidateQueries({
            queryKey: ["usuarioInfo", usuarioInfo.id],
          });
        }
        setFeedback("");
        setIsStreaming(false);
        setSessionId(evaluationPayload.session_id);
        abortControllerRef.current = null;
        onComplete?.(evaluationPayload.session_id);
      } catch (err) {
        // Verificar si es un error de validación de documento
        const error = err as Error & { error_code?: string; isValidationError?: boolean };
        if (error?.isValidationError && error?.error_code === 'INVALID_DOCUMENT') {
          // No crear intento para errores de validación
          const errorMessage = error.message || "El documento subido no corresponde a la evaluación esperada.";
          setError(errorMessage);
          setFeedback(`⚠️ ${errorMessage}`);
          setIsStreaming(false);
          abortControllerRef.current = null;
          onErrorCallback?.(error);
          return;
        }
        
        // Para otros errores, manejar normalmente
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Error desconocido en la evaluación";
        setError(errorMessage);
        setFeedback(`❌ Error: ${errorMessage}`);
        setIsStreaming(false);
        abortControllerRef.current = null;
        onErrorCallback?.(
          err instanceof Error ? err : new Error(errorMessage)
        );
      }
    },
    [
      context,
      createIntento,
      queryClient,
      usuarioInfo?.id,
      onStreamStart,
      onFirstChunk,
      onComplete,
      onErrorCallback,
    ]
  );

  return {
    evaluarConStreaming,
    feedback,
    retroContent,
    isStreaming,
    sessionId,
    error,
    resetState,
  };
}

